import { __assign, __awaiter, __generator } from "tslib";
import { TicketPriority, ItemType } from '../domain';
import { createMultiLingualCustomFieldsApi, createMultilevelCustomFieldsApi } from '../custom-fields';
import { createFilesApi } from '../files';
export var SearchResultType;
(function (SearchResultType) {
    SearchResultType[SearchResultType["PAGINATED"] = 1] = "PAGINATED";
    SearchResultType[SearchResultType["OFFSET"] = 2] = "OFFSET";
})(SearchResultType || (SearchResultType = {}));
export var SavedFilterContext;
(function (SavedFilterContext) {
    SavedFilterContext[SavedFilterContext["TICKET_FILTER"] = 0] = "TICKET_FILTER";
    SavedFilterContext[SavedFilterContext["CHAT_FILTER"] = 10] = "CHAT_FILTER";
})(SavedFilterContext || (SavedFilterContext = {}));
export var PRESENCE_TTL = 2; // minutes (older presence info is removed)
export var ChatTransferType;
(function (ChatTransferType) {
    ChatTransferType[ChatTransferType["ONLINE"] = 0] = "ONLINE";
    ChatTransferType[ChatTransferType["OFFLINE"] = 1] = "OFFLINE";
})(ChatTransferType || (ChatTransferType = {}));
var ticketsRelativePath = '/tickets';
var savedRepliesRelativePath = '/savedReplies';
export var createTicketsApi = function (baseUrl, http) {
    var apiUrl = baseUrl + ticketsRelativePath;
    var savedRepliesApiUrl = baseUrl + savedRepliesRelativePath;
    var api = {
        getTicket: function (id) {
            return http.get(apiUrl + "/" + id);
        },
        getExtendedTicket: function (id) {
            return http.get(apiUrl + "/" + id + "/admin");
        },
        addTicketCallNote: function (dto) {
            return http.post(apiUrl + "/" + dto.ticketId + "/calls/" + dto.callId + "/notes", dto);
        },
        updateTicketCallNote: function (dto) {
            return http.put(apiUrl + "/" + dto.ticketId + "/calls/" + dto.callId + "/notes/" + dto.noteId, { content: dto.content });
        },
        deleteTicketCallNote: function (dto) {
            return http.delete(apiUrl + "/" + dto.ticketId + "/calls/" + dto.callId + "/notes/" + dto.noteId);
        },
        requestCallback: function (dto) {
            return http.post(apiUrl + "/callbackRequests", dto);
        },
        cancelCallback: function (dto) {
            return http.post(apiUrl + "/" + dto.ticketId + "/calls/" + dto.callId + "/cancelCallback", {});
        },
        transferCallToNumber: function (dto) {
            var requestDto = { toNumber: dto.toNumber, lineId: dto.lineId };
            return http.post(apiUrl + "/" + dto.ticketId + "/calls/" + dto.phoneCallId + "/transferToNumber", requestDto);
        },
        getTransferHistory: function (dto) {
            return http.get(apiUrl + "/" + dto.ticketId + "/calls/" + dto.callId + "/transferHistory");
        },
        getTicketByNumber: function (dto) {
            return http.get(apiUrl + "/byNumber/" + dto.ticketNumber + "/admin");
        },
        searchAdmin: function (dto) {
            return http.post(apiUrl + "/search/admin", dto);
        },
        searchAdminIncludeConversation: function (dto) {
            return http.post(apiUrl + "/search/admin/includeConversation", dto);
        },
        syncCall: function (dto) {
            return http.post(apiUrl + "/" + dto.ticketId + "/calls/" + dto.callId + "/sync", {});
        },
        transferTicket: function (dto) {
            return http.post(apiUrl + "/" + dto.ticketId + "/transfer", { targetUserId: dto.targetUserId });
        },
        replyTickets: function (dto) {
            return http.post(apiUrl + "/agentReplies", dto);
        },
        replyTicketsNew: function (dto) {
            return http.post(apiUrl + "/agentReplies/new", dto);
        },
        replyTicket: function (dto) {
            return http.post(apiUrl + "/" + dto.id + "/agentReply", dto);
        },
        relationsTickets: function (dto) {
            return http.post(apiUrl + "/relations", dto);
        },
        removeRelations: function (dto) {
            return http.post(apiUrl + "/" + dto.id + "/relations/remove", { relatedArticleIds: dto.relatedArticleIds });
        },
        relationsTicket: function (dto) {
            return http.post(apiUrl + "/" + dto.id + "/relations", dto);
        },
        setTicketsStatus: function (dto) {
            return http.post(apiUrl + "/setStatus", dto);
        },
        setTicketStatus: function (dto) {
            return http.put(apiUrl + "/" + dto.id + "/status", dto);
        },
        assignTickets: function (dto) {
            return http.post(apiUrl + "/assign", dto);
        },
        setTicketsPriority: function (dto) {
            return http.post(apiUrl + "/setPriority", dto);
        },
        markTicketsAsSpam: function (dto) {
            return http.post(apiUrl + "/markSpam", dto);
        },
        markTicketsAsHam: function (dto) {
            return http.post(apiUrl + "/markHam", dto);
        },
        deleteTickets: function (dto) {
            return http.post(apiUrl + "/delete", dto);
        },
        updateTicketsLabels: function (dto) {
            return http.put(apiUrl + "/labels", dto);
        },
        getSavedFilter: function (dto) {
            var id = dto.id;
            return http.get(baseUrl + "/savedFilters/" + id);
        },
        getSavedFilters: function (dto) {
            var locale = dto.locale, context = dto.context;
            var maybeContext = context ? "&context=" + context : '';
            return http.get(baseUrl + "/savedFilters?locale=" + locale + maybeContext);
        },
        getSavedFiltersNew: function (dto) {
            return http.post(baseUrl + "/savedFilters/new", dto);
        },
        getSavedFiltersPerLocale: function (dto) {
            var locales = dto.locales, context = dto.context;
            return http.get(baseUrl + "/savedFilters/perLocale?locales=" + locales + "&context=" + context);
        },
        updateSavedFilter: function (dto) {
            var id = dto.id;
            return http.put(baseUrl + "/savedFilters/" + id, dto);
        },
        updateSavedFilterNew: function (dto) {
            var id = dto.id;
            return http.put(baseUrl + "/savedFilters/" + id + "/new", dto);
        },
        deleteSavedFilter: function (dto) {
            var id = dto.id;
            return http.delete(baseUrl + "/savedFilters/" + id);
        },
        addSavedFilter: function (dto) {
            return http.post(baseUrl + "/savedFilters", dto);
        },
        renameSavedFilter: function (dto) {
            var id = dto.id;
            return http.put(baseUrl + "/savedFilters/" + id + "/rename", dto);
        },
        getPresenceInfoList: function (dto) {
            var dtoWithTtl = __assign(__assign({}, dto), { maxAge: PRESENCE_TTL });
            return http.post(apiUrl + "/presence", dtoWithTtl);
        },
        addTicket: function (dto) {
            return http.post(apiUrl + "/outbound", dto);
        },
        addTicketOnBehalf: function (dto) {
            return http.post(apiUrl + "/onBehalf", dto);
        },
        getCustomFields: function () {
            return http.get(baseUrl + "/fields");
        },
        updateTicketCustomFields: function (dto) {
            return http.put(apiUrl + "/" + dto.id + "/fields", __assign(__assign({}, dto), { id: undefined }));
        },
        getFiltersTicketsCount: function (dto) {
            return http.post(apiUrl + "/filtersCounts", dto);
        },
        getFiltersTicketsCountNew: function (dto) {
            return http.post(apiUrl + "/filtersCounts/new", dto);
        },
        getTicketTimeline: function (dto) { return __awaiter(void 0, void 0, void 0, function () {
            var timeline, ticket, normalizedTicket, normalizedTimeline;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, http.get(apiUrl + "/" + dto.ticketId + "/timeline")];
                    case 1:
                        timeline = _a.sent();
                        ticket = timeline.ticket;
                        normalizedTicket = __assign(__assign({}, ticket), { labels: ticket.labels || [], priority: ticket.priority || TicketPriority.NORMAL });
                        normalizedTimeline = __assign(__assign({}, timeline), { ticket: normalizedTicket });
                        return [2 /*return*/, normalizedTimeline];
                }
            });
        }); },
        addTimelineEvent: function (dto) { return __awaiter(void 0, void 0, void 0, function () {
            var customText, ticketId;
            return __generator(this, function (_a) {
                customText = dto.customText, ticketId = dto.ticketId;
                return [2 /*return*/, http.post(apiUrl + "/" + ticketId + "/timeline", { customText: customText })];
            });
        }); },
        applyMacro: function (dto) {
            var ticketId = dto.ticketId, macroId = dto.macroId;
            return http.post(apiUrl + "/" + ticketId + "/macros/" + macroId + "/execute", { macroId: macroId });
        },
        customFields: createMultiLingualCustomFieldsApi("" + apiUrl, http, ItemType.TICKET),
        multilevelFields: createMultilevelCustomFieldsApi("" + apiUrl, http, ItemType.TICKET),
        getSavedReplies: function (dto) {
            return http.post(savedRepliesApiUrl + "/search", dto);
        },
        getSavedReply: function (dto) {
            return http.get(savedRepliesApiUrl + "/" + dto.id + "/admin?locale=" + dto.locale);
        },
        isTicketFollowed: function (dto) {
            return http.get(apiUrl + "/" + dto.id + "/follow");
        },
        follow: function (id) {
            return http.post(apiUrl + "/" + id + "/follow", {});
        },
        unfollow: function (id) {
            return http.delete(apiUrl + "/" + id + "/follow");
        },
        deleteCallRecording: function (dto) {
            return http.delete(apiUrl + "/" + dto.ticketId + "/calls/" + dto.callId + "/deleteRecording");
        },
        updateReplyContent: function (dto) {
            return http.put(apiUrl + "/" + dto.id + "/replies/" + dto.replyId, dto);
        },
        setLocale: function (dto) {
            return http.put(apiUrl + "/" + dto.ticketId + "/locale", { newLocale: dto.newLocale });
        },
        updateTicketContent: function (dto) {
            var id = dto.id, content = dto.content, subject = dto.subject;
            return http.put(apiUrl + "/" + id, { content: content, subject: subject });
        },
        relatedArticles: function (ticketId) {
            return http.get(apiUrl + "/" + ticketId + "/relations/admin");
        },
        session: {
            startForAgent: function (dto) { return http.post(apiUrl + "/" + dto.id + "/startHandling", dto); },
            endForAgent: function (dto) { return http.post(apiUrl + "/" + dto.id + "/endHandling/new", dto); },
            endForAll: function (dto) { return http.post(apiUrl + "/" + dto.id + "/endAllHandling/new", dto); },
            bulkEndForAgent: function (dto) { return http.post(apiUrl + "/endHandling/bulk", dto); },
            bulkEndForAll: function (dto) { return http.post(apiUrl + "/endAllHandling/bulk", dto); },
        },
        reportTicketPresenceStatus: function (dto) {
            return http.post(apiUrl + "/" + dto.id + "/presence", dto);
        },
        updateTicketCompany: function (dto) {
            return http.put(apiUrl + "/company", { ids: [dto.id], companyId: dto.companyId });
        },
        updateTicketAndAgentCompany: function (dto) {
            return http.put(apiUrl + "/" + dto.id + "/setCompanyOnTicketAndUser", { companyId: dto.companyId });
        },
        snoozeTicket: function (dto) { return http.post(apiUrl + "/snooze", dto); },
        unsnoozeTicket: function (dto) { return http.post(apiUrl + "/unSnooze", dto); },
        mergeTickets: function (dto) {
            return http.post(apiUrl + "/merge", dto);
        },
        uploadFile: function (dto, progressCallback) {
            return createFilesApi(apiUrl + "/" + dto.ticketId + "/files", http).uploadFileNew(dto, progressCallback);
        },
        checkFileStatus: function (dto) {
            return createFilesApi(apiUrl + "/" + dto.ticketId + "/files", http).checkFileStatus(dto);
        },
        addAgentInternalNote: function (dto) {
            return http.post(apiUrl + "/" + dto.id + "/agentInternalNote", dto);
        },
        addAgentInternalNotes: function (dto) {
            return http.post(apiUrl + "/agentInternalNotes", dto);
        },
        getReplyRawEmail: function (dto) {
            return http.get(apiUrl + "/" + dto.ticketId + "/replies/" + dto.replyId + "/rawEmail");
        },
        updateTicketBrand: function (dto) {
            return http.put(apiUrl + "/" + dto.ticketId + "/brand", { brandId: dto.brandId });
        },
        getTicketAutoRoutingAssignments: function (ticketId) {
            return http.get(apiUrl + "/" + ticketId + "/autoRouting/assignments");
        },
        transferChat: function (dto) {
            return http.post(apiUrl + "/" + dto.ticketId + "/chatTransfer", dto);
        },
    };
    return api;
};
//# sourceMappingURL=index.js.map